import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import style from '../forecasting-grid.module.less';
import messages from '../messages';

/**
 * Expander column component for the forecasting grid
 * @param {object} props - Component props
 * @param {object} props.row - The row data
 * @param {{[key: string]: boolean}} props.expandedRows - Object mapping row IDs to their expanded state
 * @param {(rowId: string, isExpanded: boolean) => void} props.handleRowExpand - Function to handle row expansion
 * @returns {React.ReactElement} The rendered Expander column component
 */
const ExpanderColumn = ({ row, expandedRows, handleRowExpand }) => {
  const intl = useIntl();
  const isExpanded = expandedRows[row.id] || false;
  return (
    <Button
      aria-expanded={isExpanded}
      aria-label={isExpanded ? intl.formatMessage(messages.collapseRowAriaLabel) : intl.formatMessage(messages.expandRowAriaLabel)}
      className={style['chart-toggle-link']}
      onClick={() => handleRowExpand(row.id, !isExpanded)}
      theme={THEME_CONSTANTS.link}
    >
      <FormattedMessage {...(isExpanded ? messages.hideChart : messages.showChart)} />
    </Button>
  );
};

/**
 * @typedef {object} ExpanderColumnDefinition
 * @property {string} id - Column identifier
 * @property {string} Header - Column header text
 * @property {string} className - CSS class name
 * @property {React.ComponentType} Cell - Cell component
 * @property {number} width - Column width
 */

/**
 * Creates the expander column definition
 * @param {{[key: string]: boolean}} expandedRows - Object mapping row IDs to their expanded state
 * @param {(rowId: string, isExpanded: boolean) => void} handleRowExpand - Function to handle row expansion
 * @returns {ExpanderColumnDefinition} The expander column definition
 */
export const createExpanderColumnDefinition = (expandedRows, handleRowExpand) => ({
  id: 'expander',
  Header: '',
  className: 'expander-column',
  Cell: ({ row }) => <ExpanderColumn expandedRows={expandedRows} handleRowExpand={handleRowExpand} row={row} />,
  width: 1,
});
